import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import type { Inspection } from '@/types/crm';

export function useInspections(projectId?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch inspections for a project or all inspections for inspector
  const { data: inspections, isLoading } = useQuery({
    queryKey: ['inspections', projectId],
    queryFn: async () => {
      let query = supabase
        .from('inspections')
        .select('*, inspector:portal_users(*), project:projects(*)') 
        .order('created_at', { ascending: false });

      if (projectId) {
        query = query.eq('project_id', projectId);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return (data || []) as unknown as Inspection[];
    },
  });

  // Create inspection
  const createInspection = useMutation({
    mutationFn: async (newInspection: Omit<Inspection, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('inspections')
        .insert([newInspection])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspections'] });
      toast({
        title: 'Inspectie aangemaakt',
        description: 'De inspectie is succesvol aangemaakt',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Fout bij aanmaken',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update inspection
  const updateInspection = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Inspection> & { id: string }) => {
      const { data, error } = await supabase
        .from('inspections')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspections'] });
      toast({
        title: 'Inspectie bijgewerkt',
        description: 'De inspectie is succesvol bijgewerkt',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Fout bij bijwerken',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete inspection
  const deleteInspection = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('inspections')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspections'] });
      toast({
        title: 'Inspectie verwijderd',
        description: 'De inspectie is succesvol verwijderd',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Fout bij verwijderen',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    inspections,
    isLoading,
    createInspection: createInspection.mutateAsync,
    updateInspection: updateInspection.mutateAsync,
    deleteInspection: deleteInspection.mutateAsync,
  };
}
